import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useApolloClient } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { format } from 'date-fns';

// Components
import ModalContainer from '../../containers/ModalContainer';
import { Typography } from '../../../display/Typography';
import { Loader } from '../../../inputs/Loader';
import { CheckBox } from '../../../inputs/CheckBox';
import { InputRadioTagTable } from '../../../display/tables/InputRadioTagTable';
import { tableProps as InputRadioTagTableProps } from '../../../display/tables/InputRadioTagTable/types';
import { tableIds } from '../../../display/tables/types';
import SegmentList from '../../../../pages/main/surveillanceRoute/components/SegmentList';

// Hooks
import { useRoadNamesById } from '../../../../hooks/useRoadDirectionLabels';

// Data
import {
  GET_MOVE_TARGET_SEGMENTS,
  GET_ROUTE_MOVE_TARGET_PAGE,
} from '../../../../gql/queries/surveillanceRoute';
import { loadingData } from '../../../../state/actions/ui/loadingData';
import { backendErrorCodes } from '../../../../gql/retryableBackendCodes';

// Utils
import { adaptTransferTargetToOrder } from '../../../../utils/surveillanceRoute';
import { roadIdsInvolved } from '../../../../utils/roadStretches';
import {
  EMPTY_RANGE,
  isNoConnectionError,
  MoveOperation,
  moveOperationFor,
  movePreview,
  nextRangeSelection,
  rangeNumbers,
  rangeProblem,
  RangeSelection,
} from '../../../../utils/moveSegments';
import {
  TARGET_HIDDEN_COLUMN_IDS,
  TARGET_HIDDEN_FILTER_IDS,
} from '../TransferSegmentModal/targetTable';
import {
  FetchTargetPage,
  TargetPage,
  useTargetPage,
} from '../TransferSegmentModal/useTargetPage';

// Types
import { ModalProps } from './types';
import {
  neighbourTime,
  pkWarningKey,
} from '../TransferSegmentModal/targetTexts';
import type {
  MoveRouteSegmentsInput,
  RouteSegment,
  SegmentTransferTarget,
} from '../../../../types/surveillanceRoute';

// Styles
import {
  BelowTable,
  Body,
  BoxTitle,
  ButtonContainer,
  ChoiceLabel,
  ChoiceOption,
  CloseButton,
  Container,
  ContainerLoader,
  EmptyMessage,
  InlineError,
  LilacBox,
  MainButtonStyled,
  Notice,
  NoticeIcon,
  PaginationButtonsStyled,
  RangeTable,
  Section,
  Text,
} from './styles';

const hm = (iso: string) => format(new Date(iso), 'HH:mm');

/** El código de negocio de un rechazo del servidor (`SR_…`), si lo trae. */
const reasonOf = (error: unknown): string | null =>
  backendErrorCodes(error).find((code) => code.startsWith('SR_')) ?? null;

/**
 * «Mover tramos» (plan 2026-10-15 § 3): partir un turno grabado en el
 * recorrido equivocado, o juntar uno repartido. Con el patrón de las ventanas
 * del 15-oct, sin piezas propias:
 * 1. qué tramos — la propia tabla de Tramos: se pulsan el primero y el
 *    último (D2, el rango va seguido);
 * 2. a dónde — a un recorrido existente: la tabla de selección de siempre,
 *    con el motivo de los que no caben. 23-oct (el usuario): sin «a uno
 *    nuevo», que metía en un modal el alta de un recorrido; un recorrido se
 *    crea desde «Nuevo recorrido» y aquí solo se mueve a uno que ya existe
 *    (el servidor lo sigue admitiendo, la pantalla ya no lo ofrece);
 * 3. «Así quedará» — las dos tablas de Tramos en solo lectura;
 * 4. si el origen se queda sin tramos, la casilla de anularlo (D8), marcada.
 * El servidor revalida todo y lo hace en una transacción.
 */
export const MoveSegmentsModal = ({
  onClickClose,
  data,
}: ModalProps): JSX.Element => {
  const { t } = useTranslation(['pages', 'common', 'errors']);
  const theme = useTheme();
  const client = useApolloClient();
  const dispatch = useDispatch();
  const route = data?.route;
  const segments = useMemo(() => data?.segments ?? [], [data?.segments]);

  const [range, setRange] = useState<RangeSelection>(EMPTY_RANGE);
  const [selectedId, setSelectedId] = useState('');
  const [targetSegments, setTargetSegments] = useState<RouteSegment[] | null>(
    null
  );
  const [annulEmpty, setAnnulEmpty] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // La clave de la última operación: reintentar lo mismo no mueve dos veces,
  // y cambiar el rango o el destino estrena otra (revisión lote I1).
  const lastOperation = useRef<MoveOperation | null>(null);

  const problem = useMemo(
    () => rangeProblem(segments, range.ids),
    [segments, range.ids]
  );
  const numbers = useMemo(
    () => rangeNumbers(segments, range.ids),
    [segments, range.ids]
  );
  // En orden de hora: el servidor los ordena igual, pero así viajan como se ven.
  const orderedIds = useMemo(() => {
    const wanted = new Set(range.ids);
    return [...segments]
      .sort(
        (a, b) =>
          new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
      )
      .filter((s) => wanted.has(s.id))
      .map((s) => s.id);
  }, [segments, range.ids]);
  const rangeKey = orderedIds.join(',');

  // 2a. Destinos para el rango (los calcula el servidor con las reglas de
  // transferir), con los filtros, el orden y las páginas de las demás tablas
  // (revisión del 22-oct). Cada rango nuevo los vuelve a pedir, y solo vale
  // la respuesta de la ÚLTIMA petición: con red lenta, la del primer toque
  // (un solo tramo) podía llegar después de la del rango entero y pintar como
  // que cabe un bloque que no cabe (revisión W3 F1).
  const fetchPage = useMemo<FetchTargetPage | null>(
    () =>
      route && rangeKey && !problem
        ? async ({ filter, skip, take }) => {
            const result = await client.query<{
              routeMoveTargetPage: TargetPage;
            }>({
              query: GET_ROUTE_MOVE_TARGET_PAGE,
              variables: {
                sourceRouteId: route.id,
                segmentIds: rangeKey.split(','),
                filter,
                skip,
                take,
              },
              fetchPolicy: 'network-only',
            });
            return result.data.routeMoveTargetPage;
          }
        : null,
    [client, problem, rangeKey, route]
  );
  const {
    targets,
    totalCount,
    page,
    pageCount,
    isLoading: isLoadingTargets,
    loadError,
    onReFetchData,
    onReloadData,
    nextPage,
    previousPage,
  } = useTargetPage(tableIds.MOVE_SEGMENTS_MODAL, fetchPage);
  const targetsReason = loadError ? reasonOf(loadError) : null;
  // `offline`: sin conexión (el aviso lo dice); `error`: cualquier otro fallo.
  const targetsFailed: false | 'offline' | 'error' =
    !loadError || targetsReason
      ? false
      : isNoConnectionError(loadError)
        ? 'offline'
        : 'error';

  const selected = targets.find((target) => target.routeId === selectedId);

  // 2b. Los tramos del destino elegido, para «Así quedará».
  useEffect(() => {
    if (!selected?.fits) {
      setTargetSegments(null);
      return undefined;
    }
    let alive = true;
    setTargetSegments(null);
    client
      .query<{ routeSegments: RouteSegment[] }>({
        query: GET_MOVE_TARGET_SEGMENTS,
        variables: { surveillanceRouteId: selected.routeId },
        fetchPolicy: 'network-only',
      })
      .then((result) => {
        if (alive) setTargetSegments(result.data?.routeSegments ?? []);
      })
      .catch(() => {
        // Sin sus tramos se puede mover igual: «Así quedará» solo pierde el
        // destino.
        if (alive) setTargetSegments([]);
      });
    return () => {
      alive = false;
    };
  }, [client, selected?.fits, selected?.routeId]);

  const preview = useMemo(
    () => movePreview(segments, range.ids, targetSegments ?? []),
    [range.ids, segments, targetSegments]
  );

  const previewRoadIds = useMemo(
    () =>
      roadIdsInvolved(route?.roadIDs, [...segments, ...(targetSegments ?? [])]),
    [route?.roadIDs, segments, targetSegments]
  );
  const roadNamesById = useRoadNamesById(previewRoadIds);

  const rangeReady = orderedIds.length > 0 && !problem;
  const destinationReady = !!selected?.fits && targetSegments !== null;
  const canConfirm = rangeReady && destinationReady && !isSubmitting;

  const onConfirm = useCallback(async () => {
    if (!data?.onConfirm || !route || !canConfirm || !selected) return;
    const operation: Omit<MoveRouteSegmentsInput, 'clientOpId'> = {
      sourceRouteId: route.id,
      segmentIds: orderedIds,
      targetRouteId: selected.routeId,
    };
    if (preview.sourceEmpty) operation.annulEmptySource = annulEmpty;
    lastOperation.current = moveOperationFor(lastOperation.current, operation);
    const input: MoveRouteSegmentsInput = {
      ...operation,
      clientOpId: lastOperation.current.clientOpId,
    };
    setIsSubmitting(true);
    dispatch(loadingData(tableIds.MOVE_SEGMENTS_MODAL, true));
    try {
      await data.onConfirm(input);
      onClickClose();
    } catch {
      // El toast del error ya lo emitió el hook; el modal sigue abierto.
    } finally {
      dispatch(loadingData(tableIds.MOVE_SEGMENTS_MODAL, false));
      setIsSubmitting(false);
    }
  }, [
    annulEmpty,
    canConfirm,
    data,
    dispatch,
    onClickClose,
    orderedIds,
    preview.sourceEmpty,
    route,
    selected,
  ]);

  // La hora del tramo de al lado, con su fecha si es de otro día (30-oct).
  const when = (iso: string, sameDayAs?: string | null) => {
    const { time, date } = neighbourTime(iso, sameDayAs);
    return date
      ? t('pages:surveillance.transferSegment.position.dayOf', { time, date })
      : time;
  };
  const position = (target: SegmentTransferTarget): string => {
    // El bloque: su primer inicio y su último final.
    const byId = (id?: string) => segments.find((s) => s.id === id);
    const prevRef = byId(orderedIds[0])?.startDate;
    const lastOfBlock = byId(orderedIds[orderedIds.length - 1]);
    const nextRef = lastOfBlock?.endDate ?? lastOfBlock?.startDate;
    if (target.prevEnd && target.nextStart) {
      return t('pages:surveillance.moveSegments.position.between', {
        prev: when(target.prevEnd, prevRef),
        next: when(target.nextStart, nextRef),
      });
    }
    if (target.nextStart) {
      return t('pages:surveillance.moveSegments.position.first', {
        next: when(target.nextStart, nextRef),
      });
    }
    if (target.prevEnd) {
      return t('pages:surveillance.moveSegments.position.last', {
        prev: when(target.prevEnd, prevRef),
      });
    }
    return t('pages:surveillance.moveSegments.position.only');
  };
  const reasonText = (code?: string | null) =>
    code
      ? t(`errors:backend.${code}`, { defaultValue: t('errors:generalError') })
      : t('errors:generalError');

  const rows = useMemo(
    () => targets.map(adaptTransferTargetToOrder),
    [targets]
  );
  const tableData = useMemo(
    () => ({
      genericOrders: rows,
      filtersDataOptions: {
        concessions: [],
        capitols: [],
        subCapitols: [],
        classifications: [],
      },
      totalCount,
      pageInfo: {
        hasNextPage: page < pageCount,
        hasPreviousPage: page > 1,
      },
      loading: isLoadingTargets,
    }),
    [rows, totalCount, page, pageCount, isLoadingTargets]
  );

  const title = t('pages:surveillance.moveSegments.title');
  const heading = data?.routeRoid ? `${title} · ${data.routeRoid}` : title;

  return (
    <ModalContainer onClickClose={onClickClose} fill={theme.colors.greyBoard}>
      <Container role="dialog" aria-modal="true" aria-label={title}>
        {isSubmitting && (
          <ContainerLoader>
            <Loader />
          </ContainerLoader>
        )}
        <Body $isLoading={isSubmitting}>
          <LilacBox>
            <BoxTitle component="h2" variant="semiBold" size="M">
              {heading}
            </BoxTitle>
            <Typography size="S" colorText="greyDark">
              {t('pages:surveillance.moveSegments.intro')}
            </Typography>
          </LilacBox>

          {/* 1. Qué tramos: la propia tabla de Tramos. */}
          <Section data-testid="move-segments-range">
            {/* Sin título ni pista propios (23-oct, el usuario): la caja
                lila ya dice «Pulsa el primer y el último tramo…». */}
            <RangeTable>
              <SegmentList
                segments={segments}
                onSegmentClick={(segment) =>
                  setRange((current) =>
                    nextRangeSelection(current, segment.id, segments)
                  )
                }
                selectedSegmentIds={range.ids}
                roadNamesById={roadNamesById}
              />
            </RangeTable>
            {numbers && (
              <Text
                size="S"
                variant="semiBold"
                data-testid="move-segments-summary"
                data-range={`${numbers.first}-${numbers.last}`}
              >
                {t('pages:surveillance.moveSegments.rangeSummary', {
                  first: numbers.first,
                  last: numbers.last,
                  count: orderedIds.length,
                })}
              </Text>
            )}
            {problem && (
              <InlineError size="S" data-testid="move-segments-range-error">
                {reasonText(problem)}
              </InlineError>
            )}
          </Section>

          {/* 2. A dónde. */}
          {rangeReady && (
            <Section>
              <>
                <InputRadioTagTable
                  titleText={t('pages:surveillance.transferSegment.targets')}
                  data={tableData as unknown as InputRadioTagTableProps['data']}
                  setRowState={setSelectedId}
                  onReFetchData={onReFetchData}
                  onReloadData={onReloadData}
                  onReFetchPagination={() => undefined}
                  tableId={tableIds.MOVE_SEGMENTS_MODAL}
                  hiddenColumnIds={TARGET_HIDDEN_COLUMN_IDS}
                  hiddenFilterIds={TARGET_HIDDEN_FILTER_IDS}
                  // La tabla pinta la «Fecha final»: se filtra por ella como
                  // en «Vincular tramo» (revisión del 27-oct).
                  showEndDateFilter
                  showRouteNameColumn
                  showButtonsPagination={false}
                  onRowDoubleTap={(row) => {
                    // Solo marca: mover es un gesto aparte.
                    if (row.original.id) setSelectedId(row.original.id);
                  }}
                />
                {/* La tabla va a mitad del modal (el pie es de todo el
                      flujo): su paginación, justo debajo. */}
                <PaginationButtonsStyled
                  pageCount={pageCount}
                  currentPage={page}
                  totalCount={totalCount}
                  nextPage={nextPage}
                  previousPage={previousPage}
                  isDisabled={isLoadingTargets || isSubmitting}
                />
                {!isLoadingTargets &&
                  !targetsReason &&
                  (targetsFailed || targets.length === 0) && (
                    <EmptyMessage data-testid="move-segments-no-targets">
                      {targetsFailed === 'offline'
                        ? t('common:offline.noConnection')
                        : targetsFailed
                          ? t('errors:generalError')
                          : t('pages:surveillance.transferSegment.noTargets')}
                    </EmptyMessage>
                  )}
                <BelowTable>
                  {targetsReason && (
                    <InlineError
                      size="S"
                      data-testid="move-segments-target-reason"
                    >
                      {reasonText(targetsReason)}
                    </InlineError>
                  )}
                  {selected?.fits && (
                    <Text
                      size="S"
                      variant="semiBold"
                      data-testid="move-segments-position"
                    >
                      {position(selected)}
                    </Text>
                  )}
                  {/* 30-oct: como en «Transferir», centrado y no en caja. */}
                  {selected?.fits && selected.pkWarningCode && (
                    <InlineError
                      size="S"
                      role="status"
                      data-testid="move-segments-pk-warning"
                    >
                      {t(
                        selected.pkWarningCode ===
                          'SR_SEGMENT_PK_DIRECTION_MISMATCH'
                          ? 'pages:surveillance.moveSegments.directionWarning'
                          : pkWarningKey(selected.pkWarningCode)
                      )}
                    </InlineError>
                  )}
                  {selected && !selected.fits && (
                    <InlineError
                      size="S"
                      data-testid="move-segments-target-reason"
                    >
                      {reasonText(selected.reasonCode)}
                    </InlineError>
                  )}
                </BelowTable>
              </>
            </Section>
          )}

          {/* 3. Así quedará: las dos tablas de Tramos en solo lectura. */}
          {rangeReady && destinationReady && (
            <Section data-testid="move-segments-preview">
              <Typography size="M" variant="semiBold">
                {t('pages:surveillance.moveSegments.previewTarget', {
                  roid: selected?.roid ?? '',
                })}
              </Typography>
              {/* 30-oct (el usuario: «ya sé lo que estoy quitando»): el
                  origen ya está arriba con los tramos marcados, así que aquí
                  va solo el destino, sin más explicación: los que llegan se
                  ven resaltados. */}
              <div data-testid="move-segments-preview-target">
                <SegmentList
                  segments={preview.target}
                  selectedSegmentIds={range.ids}
                  roadNamesById={roadNamesById}
                />
              </div>
            </Section>
          )}

          {/* 4. Juntar: el origen vacío se anula si se marca (D8). */}
          {rangeReady && preview.sourceEmpty && (
            <Section>
              <ChoiceOption>
                <CheckBox
                  indeterminate={false}
                  checked={annulEmpty}
                  onChange={(e) => setAnnulEmpty(e.target.checked)}
                  aria-label={t('pages:surveillance.moveSegments.annulEmpty')}
                  data-testid="move-segments-annul"
                />
                <ChoiceLabel size="S" component="span">
                  {t('pages:surveillance.moveSegments.annulEmpty')}
                </ChoiceLabel>
              </ChoiceOption>
              {!annulEmpty && (
                <Notice role="alert">
                  <NoticeIcon aria-hidden="true">⚠</NoticeIcon>
                  <span>
                    {t('pages:surveillance.transferSegment.warningEmpty')}
                  </span>
                </Notice>
              )}
            </Section>
          )}

          <ButtonContainer>
            <CloseButton
              type="button"
              text={t('common:buttons.back')}
              variant="secondary"
              onClick={onClickClose}
              disabled={isSubmitting}
            />
            <MainButtonStyled
              type="button"
              text={t('pages:surveillance.moveSegments.confirm')}
              onClick={() => void onConfirm()}
              disabled={!canConfirm}
              data-testid="move-segments-confirm"
            />
          </ButtonContainer>
        </Body>
      </Container>
    </ModalContainer>
  );
};

export default MoveSegmentsModal;
